import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  Clock, MapPin, Calendar, Bell, FileText,
  ChevronRight, Play, Square, AlertCircle, CheckCircle,
  Home, Users, LogOut, Phone, MessageSquare,
  Navigation, AlertTriangle, Shield, Sun, Moon, CloudSun,
  Briefcase, DollarSign, BookOpen, Inbox, Timer,
  Check, X, ChevronDown, ChevronUp, User, Heart
} from "lucide-react";
import CommunicationBar from "../../components/employee/CommunicationBar";
import EmergencyModal from "../../components/employee/EmergencyModal";
import MessageOfficeModal from "../../components/employee/MessageOfficeModal";

// Format duration from minutes
const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

// Current Visit Card - Primary focus when clocked in
const CurrentVisitCard = ({ visit, onClockOut, clockingOut, onNavigate }) => {
  const clockInTime = visit?.clock_in?.timestamp 
    ? new Date(visit.clock_in.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  
  const duration = visit?.clock_in?.timestamp
    ? Math.floor((Date.now() - new Date(visit.clock_in.timestamp).getTime()) / 60000)
    : 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-emerald-200 overflow-hidden" data-testid="current-visit-card">
      {/* Status Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
            <span className="font-semibold">Active Visit</span>
          </div>
          <div className="text-emerald-100 text-sm">
            {formatDuration(duration)} elapsed
          </div>
        </div>
      </div>

      {/* Visit Details */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{visit.client_name}</h2>
            {visit.client?.address && (
              <div className="flex items-center gap-1.5 text-gray-500 text-sm mt-1">
                <MapPin className="w-4 h-4" />
                <span>{visit.client.address}</span>
              </div>
            )}
          </div>
          {clockInTime && (
            <div className="text-right">
              <div className="text-xs text-gray-500">Clocked in at</div>
              <div className="font-semibold text-gray-900">{clockInTime}</div>
            </div>
          )}
        </div>

        {/* Primary Actions */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Link
            to={`/employee/visit/${visit.id}/workflow`}
            className="py-3.5 px-4 rounded-xl bg-[#06372B] text-white font-semibold text-center flex items-center justify-center gap-2"
            data-testid="visit-details-btn"
          >
            <FileText className="w-4 h-4" />
            Visit Details
          </Link>
          <button
            onClick={onClockOut}
            disabled={clockingOut}
            className="py-3.5 px-4 rounded-xl bg-red-50 text-red-700 font-semibold flex items-center justify-center gap-2 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-60"
            data-testid="clock-out-btn"
          >
            <Square className="w-4 h-4" />
            {clockingOut ? "..." : "Clock Out"}
          </button>
        </div>

        {/* Secondary Actions */}
        <div className="flex gap-2">
          {visit.client?.address && (
            <button
              onClick={() => onNavigate(visit.client.address)}
              className="flex-1 py-2.5 px-3 rounded-xl bg-blue-50 text-blue-700 font-medium text-sm flex items-center justify-center gap-1.5 hover:bg-blue-100 transition-colors"
              data-testid="navigate-btn"
            >
              <Navigation className="w-4 h-4" />
              Navigate
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Next Visit Card - Primary focus when not clocked in
const NextVisitCard = ({ visit, onStartShift, startingShift, onNavigate }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-[#06372B]/20 overflow-hidden" data-testid="next-visit-card">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#06372B] to-[#0a4a3a] px-5 py-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white/70 text-sm">Next Visit</div>
            <div className="font-semibold text-lg">{visit.client_name}</div>
          </div>
          <div className="text-right">
            <div className="text-white/70 text-sm">Scheduled</div>
            <div className="font-semibold">{visit.start_time} - {visit.end_time}</div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="p-5">
        {visit.client_address && (
          <div className="flex items-start gap-2 mb-4 p-3 bg-gray-50 rounded-xl">
            <MapPin className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500 mb-0.5">Address</div>
              <div className="text-gray-900">{visit.client_address}</div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={onStartShift}
            disabled={startingShift}
            className="w-full py-4 px-4 rounded-xl bg-[#06372B] text-white font-semibold text-lg flex items-center justify-center gap-2 hover:bg-[#0a4a3a] transition-colors disabled:opacity-60"
            data-testid="start-shift-btn"
          >
            <Play className="w-5 h-5" />
            {startingShift ? "Getting Location..." : "Start My Shift"}
          </button>
          
          {visit.client_address && (
            <button
              onClick={() => onNavigate(visit.client_address)}
              className="w-full py-3 px-4 rounded-xl bg-blue-50 text-blue-700 font-medium flex items-center justify-center gap-2 hover:bg-blue-100 transition-colors"
              data-testid="navigate-btn"
            >
              <Navigation className="w-4 h-4" />
              Navigate to Client
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Assignment Offer Card - Detailed and actionable
const AssignmentOfferCard = ({ offer, onAccept, onDecline, onViewDetails, expanded, onToggle }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden" data-testid={`assignment-offer-${offer.id}`}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between bg-amber-50 border-b border-amber-100"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-amber-700" />
          </div>
          <div className="text-left">
            <div className="font-semibold text-amber-900">New Assignment Offer</div>
            <div className="text-sm text-amber-700">{offer.client_name || "New Client"}</div>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-amber-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-amber-600" />
        )}
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="p-5">
          <div className="grid grid-cols-2 gap-4 mb-4">
            {offer.schedule_summary && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Schedule</div>
                <div className="font-medium text-gray-900">{offer.schedule_summary}</div>
              </div>
            )}
            {offer.weekly_hours && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Weekly Hours</div>
                <div className="font-medium text-gray-900">{offer.weekly_hours}h/week</div>
              </div>
            )}
            {offer.start_date && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Start Date</div>
                <div className="font-medium text-gray-900">
                  {new Date(offer.start_date).toLocaleDateString()}
                </div>
              </div>
            )}
            {offer.distance_miles && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Distance</div>
                <div className="font-medium text-gray-900">{offer.distance_miles.toFixed(1)} miles</div>
              </div>
            )}
          </div>

          {offer.client_address && (
            <div className="p-3 bg-gray-50 rounded-xl mb-4">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                <span className="text-sm text-gray-600">{offer.client_address}</span>
              </div>
            </div>
          )}

          {/* Expiration Warning */}
          {offer.offer_expires_at && (
            <div className="flex items-center gap-2 text-sm text-amber-700 mb-4">
              <Clock className="w-4 h-4" />
              <span>
                Expires {new Date(offer.offer_expires_at).toLocaleDateString()} at{" "}
                {new Date(offer.offer_expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => onDecline(offer.id)}
              className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
              data-testid={`decline-${offer.id}`}
            >
              <X className="w-4 h-4" />
              Decline
            </button>
            <button
              onClick={() => onAccept(offer.id)}
              className="flex-1 py-3 px-4 rounded-xl bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors"
              data-testid={`accept-${offer.id}`}
            >
              <Check className="w-4 h-4" />
              Accept
            </button>
          </div>

          <button
            onClick={() => onViewDetails(offer.id)}
            className="w-full mt-3 py-2.5 text-[#06372B] font-medium text-sm hover:underline"
            data-testid={`view-details-${offer.id}`}
          >
            Review Full Details →
          </button>
        </div>
      )}
    </div>
  );
};

// Alert Card Component
const AlertCard = ({ alert }) => {
  const colorClasses = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
  };

  const iconMap = {
    credential_expired: AlertTriangle,
    credential_expiring: Clock,
    missing_documentation: FileText,
    notification: MessageSquare,
    training: BookOpen,
    schedule_change: Calendar,
  };

  const IconComponent = iconMap[alert.card_type] || AlertCircle;

  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${colorClasses[alert.color] || colorClasses.blue}`}>
      <IconComponent className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-medium">{alert.title}</div>
        {alert.body && <div className="text-sm opacity-80 mt-0.5">{alert.body}</div>}
        {alert.action_url && (
          <Link to={alert.action_url} className="text-sm font-medium mt-2 inline-block hover:underline">
            {alert.action_label || "View"} →
          </Link>
        )}
      </div>
    </div>
  );
};

// Today's Schedule Summary
const TodaySchedule = ({ schedule, currentVisitId }) => {
  if (!schedule || schedule.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          <h3 className="font-semibold">Today&apos;s Schedule</h3>
        </div>
        <span className="text-sm text-gray-500">{schedule.length} visits</span>
      </div>
      <div className="divide-y divide-gray-100">
        {schedule.map((visit) => (
          <div
            key={visit.visit_id}
            className={`px-5 py-3 flex items-center justify-between ${
              visit.visit_id === currentVisitId ? "bg-emerald-50" : ""
            }`}
          >
            <div className="flex items-center gap-3">
              {visit.status === "completed" ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : visit.visit_id === currentVisitId ? (
                <div className="w-5 h-5 rounded-full border-2 border-emerald-500 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
              )}
              <div>
                <div className="font-medium text-gray-900">{visit.client_name}</div>
                <div className="text-sm text-gray-500">
                  {visit.start_time} - {visit.end_time}
                </div>
              </div>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              visit.status === "completed" ? "bg-emerald-100 text-emerald-700" :
              visit.status === "clocked_in" || visit.status === "in_progress" ? "bg-blue-100 text-blue-700" :
              "bg-gray-100 text-gray-600"
            }`}>
              {visit.status.replace(/_/g, " ")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Quick Links Section
const QuickLinks = () => {
  const links = [
    { to: "/employee/schedule", icon: Calendar, label: "My Schedule", desc: "Week view & upcoming" },
    { to: "/employee/clients", icon: Users, label: "My Clients", desc: "Care plans & info" },
    { to: "/employee/visits", icon: Clock, label: "Visit History", desc: "Past visits & notes" },
    { to: "/employee/documents", icon: FileText, label: "My Documents", desc: "Certifications & files" },
    { to: "/employee/training", icon: BookOpen, label: "Training", desc: "Learning materials" },
    { to: "/employee/messages", icon: Inbox, label: "Messages", desc: "Office communication" },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="font-semibold">Quick Links</h3>
      </div>
      <div className="grid grid-cols-3 gap-px bg-gray-100">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="bg-white p-4 flex flex-col items-center text-center hover:bg-gray-50 transition-colors"
            data-testid={`quick-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-2">
              <link.icon className="w-6 h-6 text-gray-600" />
            </div>
            <div className="font-medium text-sm text-gray-900">{link.label}</div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// Main Workspace Component
export default function CaregiverWorkspace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Action states
  const [clockingOut, setClockingOut] = useState(false);
  const [startingShift, setStartingShift] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [expandedOffer, setExpandedOffer] = useState(null);

  // Modal states
  const [showEmergency, setShowEmergency] = useState(false);
  const [showMessageOffice, setShowMessageOffice] = useState(false);

  const loadWorkspace = useCallback(async () => {
    try {
      setError(null);
      const { data } = await api.get("/caregiver-workspace/home");
      setWorkspace(data);
      // Auto-expand first offer if any
      const offers = data.cards?.filter(c => c.card_type === "assignment_offer") || [];
      if (offers.length > 0 && !expandedOffer) {
        setExpandedOffer(offers[0].related_entity_id);
      }
    } catch (err) {
      console.error("Failed to load workspace:", err);
      setError(err.response?.data?.detail || "Failed to load workspace");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspace();
    // Refresh every 60 seconds
    const interval = setInterval(loadWorkspace, 60000);
    return () => clearInterval(interval);
  }, [loadWorkspace]);

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  };

  const handleStartShift = async (visitId) => {
    setStartingShift(true);
    setGpsError(null);

    try {
      const gps = await getCurrentPosition();
      const { data } = await api.post(`/caregiver-workspace/visit/${visitId}/clock-in`, {
        visit_id: visitId,
        gps,
      });

      if (data.status === "gps_warning") {
        if (window.confirm(`${data.warning}\n\nProceed anyway?`)) {
          await api.post(`/caregiver-workspace/visit/${visitId}/clock-in`, {
            visit_id: visitId,
            gps,
            override_gps_warning: true,
            override_reason: "Confirmed at correct location",
          });
        } else {
          setStartingShift(false);
          return;
        }
      }

      // Navigate to workflow
      navigate(`/employee/visit/${visitId}/workflow`);
    } catch (err) {
      if (err.code === 1) {
        setGpsError("Location access denied. Please enable GPS.");
      } else if (err.code === 2) {
        setGpsError("Location unavailable. Please try again.");
      } else {
        setGpsError(err.response?.data?.detail || "Could not start shift");
      }
    } finally {
      setStartingShift(false);
    }
  };

  const handleClockOut = async () => {
    if (!workspace?.active_visit) return;

    setClockingOut(true);
    setGpsError(null);

    try {
      const gps = await getCurrentPosition();
      await api.post(`/caregiver-workspace/visit/${workspace.active_visit.id}/clock-out`, {
        visit_id: workspace.active_visit.id,
        gps,
      });

      await loadWorkspace();
    } catch (err) {
      setGpsError(err.response?.data?.detail || "Clock out failed");
    } finally {
      setClockingOut(false);
    }
  };

  const handleAcceptOffer = async (offerId) => {
    try {
      await api.post(`/assignments/${offerId}/accept`);
      await loadWorkspace();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not accept offer");
    }
  };

  const handleDeclineOffer = async (offerId) => {
    if (!window.confirm("Are you sure you want to decline this assignment?")) return;
    try {
      await api.post(`/assignments/${offerId}/decline`, { reason: "declined_by_caregiver" });
      await loadWorkspace();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not decline offer");
    }
  };

  const openMaps = (address) => {
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, "_blank");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/employee");
  };

  // Get time-appropriate greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: "Good morning", icon: Sun };
    if (hour < 17) return { text: "Good afternoon", icon: CloudSun };
    return { text: "Good evening", icon: Moon };
  };

  const { text: greetingText, icon: GreetingIcon } = getGreeting();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-[#06372B] border-t-transparent animate-spin mx-auto" />
          <div className="text-gray-500 mt-4">Loading your day...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <div className="text-gray-900 font-medium mt-4">Something went wrong</div>
          <div className="text-gray-500 text-sm mt-1">{error}</div>
          <button
            onClick={loadWorkspace}
            className="mt-4 px-4 py-2 bg-[#06372B] text-white rounded-lg font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { cards, today_schedule, visits_today, visits_completed, active_visit } = workspace || {};

  // Separate cards by type
  const assignmentOffers = (cards || []).filter(c => c.card_type === "assignment_offer");
  const alerts = (cards || []).filter(c => 
    ["credential_expired", "credential_expiring", "missing_documentation", "notification", "training", "schedule_change"].includes(c.card_type)
  );

  // Find next visit if not clocked in
  const nextVisit = !active_visit && today_schedule?.find(v => 
    v.status === "scheduled" || v.status === "en_route"
  );

  const hasNoWorkToday = !active_visit && !nextVisit && assignmentOffers.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#06372B] to-[#0a4a3a] text-white px-4 py-6 sticky top-0 z-40">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center">
                <GreetingIcon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-white/70 text-sm">{greetingText}</div>
                <div className="font-semibold text-lg">{user?.name || "Caregiver"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Link
                to="/employee/notifications"
                className="relative p-2.5 rounded-full hover:bg-white/10 transition-colors"
                data-testid="notifications-link"
              >
                <Bell className="w-5 h-5" />
              </Link>
              <button
                onClick={handleLogout}
                className="p-2.5 rounded-full hover:bg-white/10 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Hero Question */}
          <div className="text-center py-2">
            <h1 className="text-xl font-bold">What do I need to do today?</h1>
            {visits_today > 0 && (
              <div className="mt-2 text-white/70 text-sm">
                {visits_completed || 0} of {visits_today} visits completed
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* GPS Error */}
        {gpsError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <div>
              <div className="font-medium text-red-800">Location Error</div>
              <div className="text-sm text-red-700">{gpsError}</div>
            </div>
            <button onClick={() => setGpsError(null)} className="ml-auto">
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}

        {/* SECTION 1: Today's Schedule / Current Visit */}
        {active_visit ? (
          <CurrentVisitCard
            visit={active_visit}
            onClockOut={handleClockOut}
            clockingOut={clockingOut}
            onNavigate={openMaps}
          />
        ) : nextVisit ? (
          <NextVisitCard
            visit={nextVisit}
            onStartShift={() => handleStartShift(nextVisit.visit_id)}
            startingShift={startingShift}
            onNavigate={openMaps}
          />
        ) : hasNoWorkToday ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="font-semibold text-gray-900">No Visits Today</h2>
            <p className="text-gray-500 text-sm mt-1">
              Check your full schedule or contact the office
            </p>
            <Link
              to="/employee/schedule"
              className="mt-4 inline-block py-2.5 px-5 bg-[#06372B] text-white rounded-xl font-medium"
            >
              View Full Schedule
            </Link>
          </div>
        ) : null}

        {/* Show "No visits today" notice when there are offers but no scheduled visits */}
        {!active_visit && !nextVisit && assignmentOffers.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>No visits scheduled for today</span>
            <Link to="/employee/schedule" className="ml-auto text-[#06372B] font-medium">
              View Schedule
            </Link>
          </div>
        )}

        {/* Today's Schedule Summary (if multiple visits) */}
        {today_schedule && today_schedule.length > 1 && (
          <TodaySchedule 
            schedule={today_schedule} 
            currentVisitId={active_visit?.id}
          />
        )}

        {/* SECTION 2: Pending Assignment Offers */}
        {assignmentOffers.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Pending Assignment Offers
              <span className="ml-auto text-sm font-normal text-amber-600">
                {assignmentOffers.length} pending
              </span>
            </h3>
            {assignmentOffers.map((offer) => (
              <AssignmentOfferCard
                key={offer.related_entity_id}
                offer={{
                  id: offer.related_entity_id,
                  client_name: offer.subtitle,
                  ...offer.metadata,
                  offer_expires_at: offer.expires_at,
                }}
                expanded={expandedOffer === offer.related_entity_id}
                onToggle={() => setExpandedOffer(
                  expandedOffer === offer.related_entity_id ? null : offer.related_entity_id
                )}
                onAccept={handleAcceptOffer}
                onDecline={handleDeclineOffer}
                onViewDetails={(id) => navigate(`/employee/assignments/${id}`)}
              />
            ))}
          </div>
        )}

        {/* SECTION 3: Office / Priority Alerts */}
        {alerts.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Alerts & Notifications
            </h3>
            {alerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} />
            ))}
          </div>
        )}

        {/* SECTION 4: Quick Links */}
        <QuickLinks />
      </div>

      {/* SECTION 5: Persistent Communication Bar */}
      <CommunicationBar
        onMessageOffice={() => setShowMessageOffice(true)}
        onNeedHelp={() => setShowMessageOffice(true)}
        onEmergency={() => setShowEmergency(true)}
      />

      {/* Modals */}
      {showEmergency && (
        <EmergencyModal
          onClose={() => setShowEmergency(false)}
          onSuccess={loadWorkspace}
        />
      )}

      {showMessageOffice && (
        <MessageOfficeModal
          onClose={() => setShowMessageOffice(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}
